import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import BranchGenderField from "./BranchGenderField";
import BranchLocationFields from "./BranchLocationFields";
import BranchManagerFields from "./BranchManagerFields";
import BusinessTypePicker from "./BusinessTypePicker";
import GooglePlaceCard from "./GooglePlaceCard";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { focusFirstError } from "../services/formFocus";
import { KEEP_MANAGER, MANAGER_FIELD_IDS, validateManager } from "../services/branchManager";
import { can } from "../services/permissions";
import { fetchBusinessTypes, fetchBusinessUsers } from "../services/lookupService";
import { activeLimitReached, updateBranch } from "../services/branchService";
import { TIMEZONES } from "../services/timezones";
import useBranchUsage from "../hooks/useBranchUsage";
import useBusinessGender from "../hooks/useBusinessGender";


/** A branch's own values in the shape the form and the service both use. */
const toForm = (branch) => ({
  name: branch?.name ?? "",
  code: branch?.code ?? "",
  status: branch?.status ?? "active",
  // Null on a legacy branch; the API requires one on its next save.
  gender: branch?.gender ?? "",
  address: branch?.address ?? "",
  addressLine2: branch?.address_line2 ?? "",
  region: branch?.region ?? "",
  postalCode: branch?.postal_code ?? "",
  phone: branch?.phone ?? "",
  email: branch?.email ?? "",
  capacity: branch?.capacity ?? "",
  timezone: branch?.timezone ?? "",
  description: branch?.description ?? "",
  latitude: branch?.latitude ?? "",
  longitude: branch?.longitude ?? "",
  mapUrl: branch?.map_url ?? "",
  // The manual Google place field starts empty: the linked place is shown on
  // its own, and only a new value is sent.
  googlePlaceId: "",
  cityId: branch?.city?.id ?? "",
  currencyId: branch?.currency?.id ?? "",
});

/** The ids of the types the branch is already filed under. */
const toTypeIds = (branch) => (branch?.business_types ?? []).map((type) => type.id);

/**
 * Settings for an existing branch — the address, contact details, capacity,
 * timezone and status, plus the two answers that used to cost a second screen:
 * what trades the branch is filed under, and who runs it.
 *
 * The manager block is only offered to somebody who may manage staff, which is
 * the same rule the API enforces; media and opening hours still have their own
 * flows and are deliberately not here.
 */
export default function BranchLocationModal({ branch, open, onClose, onSaved, onChanged }) {
  const { t, i18n } = useTranslation();
  // Handing a branch to somebody else is staff management, not branch editing:
  // a branch manager may keep their own branch's details right without being
  // able to replace themselves. The API refuses it either way.
  const canAssignManager = can(getCurrentUser(), "users.create");

  const [values, setValues] = useState(() => toForm(branch));
  const [businessTypeIds, setBusinessTypeIds] = useState(() => toTypeIds(branch));
  // "unchanged" until somebody says otherwise, so saving an address does not
  // reopen the manager question.
  const [manager, setManager] = useState(KEEP_MANAGER);
  // Seeded with the branch's own types so the chips are on screen before the
  // full list arrives; the fetch below replaces it with everything on offer.
  const [businessTypes, setBusinessTypes] = useState(() => branch?.business_types ?? []);
  const [users, setUsers] = useState([]);
  // OUP-26 — "the list did not load" is not "there is nobody to pick".
  const [usersFailed, setUsersFailed] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);
  // The branch as the Google card last saw it: linking and unlinking save at
  // once, ahead of the rest of the form.
  const [googleBranch, setGoogleBranch] = useState(branch);
  const { allowedGenders, loading: loadingGender } = useBusinessGender(open);
  const genderChoice = allowedGenders.length > 1;
  // A branch that is already active keeps "Active"; any other may only take it
  // while the plan has room for one more active branch.
  const { usage } = useBranchUsage(open);
  const activeLocked = branch?.status !== "active" && activeLimitReached(usage);

  useEffect(() => {
    if (!open) return;

    setValues(toForm(branch));
    setGoogleBranch(branch);
    setBusinessTypes((list) => (list.length > 0 ? list : branch?.business_types ?? []));
    setBusinessTypeIds(toTypeIds(branch));
    setManager(KEEP_MANAGER);
    setErrors({});
    setBanner("");
  }, [open, branch]);

  // The two lists the added fields need, fetched the first time the modal is
  // opened rather than with the page behind it: most visits to a branch list
  // never open it at all.
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setLoadingLookups(true);

    (async () => {
      try {
        const types = await fetchBusinessTypes();
        if (!cancelled) setBusinessTypes(types);
      } catch (err) {
        if (!cancelled) setBanner(apiErrorMessage(err, t("branchForm.loadFailed")));
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }

      // Asked for separately: a user who may not list staff can still edit the
      // branch, and their 403 must not cost them the rest of the form.
      if (!canAssignManager) return;

      try {
        const list = await fetchBusinessUsers();
        if (!cancelled) {
          setUsers(list);
          setUsersFailed(false);
        }
      } catch {
        // Listing staff needs `users.view`, which this block's own gate
        // (`users.create`) does not imply — a role may hold one without the
        // other. Say so rather than reporting an empty business.
        if (!cancelled) {
          setUsers([]);
          setUsersFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, canAssignManager, i18n.language, t]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  /** OUP-2 — API error key -> input id, in the order the fields are rendered. */
  const FIELD_IDS = {
    name: "name",
    code: "code",
    status: "status",
    address: "address",
    address_line2: "addressLine2",
    region: "region",
    postal_code: "postalCode",
    phone: "phone",
    email: "email",
    capacity: "capacity",
    timezone: "timezone",
    description: "description",
    map_url: "mapUrl",
    google_place_id: "googlePlaceId",
    business_type_ids: "businessTypeIds",
    gender: "gender",
    ...MANAGER_FIELD_IDS,
  };

  const report = (found) => {
    setErrors(found);
    focusFirstError(found, FIELD_IDS);
  };

  // The API reports list errors as business_type_ids.0 … — show the first.
  const firstErrorFor = (prefix) =>
    Object.entries(errors).find(([key]) => key.startsWith(prefix))?.[1]?.[0];

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    // Coordinates are not asked for here any more, and the API no longer
    // demands them: a branch that predates the map link keeps the location it
    // already has, and a new one gets it from the link on create.
    // The manager block is only shown — and only sent — to somebody who may
    // manage staff, so it is only checked for them either.
    const found = canAssignManager ? validateManager(manager, t) : {};

    if (!values.name.trim()) found.name = [t("branchForm.errors.name")];
    if (!String(values.address ?? "").trim()) found.address = [t("branchForm.errors.address")];
    // The API keeps the current types when none are sent, but an emptied list
    // reads as a mistake rather than as "leave them alone".
    if (businessTypeIds.length === 0) {
      found.business_type_ids = [t("branchForm.errors.businessTypes")];
    }
    if (genderChoice && !values.gender) found.gender = [t("branchForm.genderRequired")];

    if (Object.keys(found).length > 0) {
      report(found);
      setBanner(t("branchForm.errors.summary"));
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      onSaved(
        await updateBranch(branch.id, {
          ...values,
          businessTypeIds,
          manager: canAssignManager ? manager : null,
        })
      );
    } catch (err) {
      report(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("branchDetail.locationSaveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-3xl rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} className="flex max-h-[85vh] flex-col" noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-lg font-semibold">{t("branchDetail.locationTitle")}</h2>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="field-label" htmlFor="name">
                  {t("branchForm.name")} <span className="text-danger-text">*</span>
                </label>
                <input
                  id="name"
                  className="field-input"
                  value={values.name}
                  onChange={(e) => setValues({ ...values, name: e.target.value })}
                />
                {errors.name?.[0] && <p className="field-error">{errors.name[0]}</p>}
              </div>

              <BranchLocationFields
                values={values}
                onChange={setValues}
                errors={errors}
                timezones={TIMEZONES}
                activeLockedHint={
                  activeLocked ? t("branchForm.activeLocked", { limit: usage.branches_active_limit }) : ""
                }
              />

              <BranchGenderField
                allowedGenders={allowedGenders}
                value={values.gender}
                onChange={(gender) => setValues((v) => ({ ...v, gender }))}
                error={errors.gender?.[0]}
                legacy={!branch?.gender}
                loading={loadingGender}
              />

              <GooglePlaceCard
                branch={googleBranch}
                value={values.googlePlaceId}
                onChange={(googlePlaceId) => setValues((v) => ({ ...v, googlePlaceId }))}
                error={errors.google_place_id?.[0]}
                searchQuery={[getCurrentUser()?.business?.name, values.name.trim(), branch?.city?.name]
                  .filter(Boolean)
                  .join(" ")}
                lat={branch?.latitude}
                lng={branch?.longitude}
                onBranchChange={(saved) => {
                  setGoogleBranch(saved);
                  onChanged?.(saved);
                }}
              />

              <div className="md:col-span-2">
                <BusinessTypePicker
                  businessTypes={businessTypes}
                  selected={businessTypeIds}
                  onChange={setBusinessTypeIds}
                  error={firstErrorFor("business_type_ids")}
                />
              </div>

              {canAssignManager && (
                <BranchManagerFields
                  value={manager}
                  onChange={setManager}
                  users={users}
                  errors={errors}
                  loading={loadingLookups}
                  modes={["unchanged", "existing", "new"]}
                  currentManager={branch?.manager ?? null}
                  usersFailed={usersFailed}
                />
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
